import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { resolveMediaUrl } from './landingData';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export default function FeatureShowcase({ features }) {
  const [active, setActive] = useState(0);
  const scope = useRef(null);
  const imageRef = useRef(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add('(min-width: 901px) and (prefers-reduced-motion: no-preference)', () => {
      gsap.utils.toArray('.dl-feature-card').forEach((card, index) => {
        ScrollTrigger.create({
          trigger: card,
          start: 'top 52%',
          end: 'bottom 48%',
          onEnter: () => setActive(index),
          onEnterBack: () => setActive(index),
        });
      });
    });
    return () => mm.revert();
  }, { scope });

  useEffect(() => {
    if (!window.matchMedia('(max-width: 900px)').matches || !scope.current) return undefined;
    const cards = [...scope.current.querySelectorAll('.dl-feature-card')];
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setActive(cards.indexOf(visible.target));
    }, { rootMargin: '-32% 0px -32% 0px', threshold: [0.2, 0.5, 0.8] });
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [features]);

  useEffect(() => {
    if (!imageRef.current || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    gsap.fromTo(imageRef.current, { autoAlpha: 0, scale: 1.045, y: 18 }, { autoAlpha: 1, scale: 1, y: 0, duration: 0.65, ease: 'power3.out' });
  }, [active]);

  return (
    <section id="experience" className="dl-features" ref={scope}>
      <div className="dl-section-heading dl-reveal">
        <div className="dl-section-code">01 / EXPERIENCE</div>
        <h2>Całe miasto<br /><em>w Twojej dłoni.</em></h2>
        <p>Przewijaj. Telefon pokaże Ci kolejne warstwy świata VROOM.</p>
      </div>
      <div className="dl-features-layout">
        <div className="dl-feature-visual">
          <div className="dl-phone dl-phone-showcase">
            <div className="dl-phone-speaker" />
            <div className="dl-phone-screen">
              <img key={features[active]?.id} ref={imageRef} src={resolveMediaUrl(features[active]?.imageUrl)} alt={features[active]?.title || 'Funkcja aplikacji VROOM'} />
              <div className="dl-phone-sheen" />
            </div>
          </div>
          <div className="dl-feature-hud" aria-hidden="true">
            <span>MODULE</span>
            <b>{String(active + 1).padStart(2, '0')} / {String(features.length).padStart(2, '0')}</b>
          </div>
          <div className="dl-feature-progress" aria-hidden="true"><span style={{ height: `${((active + 1) / features.length) * 100}%` }} /></div>
        </div>
        <div className="dl-feature-list">
          {features.map((feature, index) => (
            <article
              className={`dl-feature-card ${index === active ? 'is-active' : ''}`}
              key={feature.id}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onClick={() => setActive(index)}
              tabIndex={0}
            >
              <span className="dl-feature-index">{String(index + 1).padStart(2, '0')}</span>
              <div><h3>{feature.title}</h3><p>{feature.description}</p></div>
              <i aria-hidden="true">↗</i>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
